import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { EmpleadoCrud } from 'src/app/pages/empleados/models/empleado';
import { FirmanteNif, TipoFirma } from '../../Envios/documentos/models/firmante-nif';
import { buildFormularioDocumentoVacio } from '../../Envios/documentos/services/formulario-documento-factory';
import {
  CODIGO_FIRMANTE_PRINCIPAL,
  MAX_FIRMANTES_ENVIO_SIMPLE,
  construirFirmanteVacio
} from '../../Envios/documentos/services/firmante-envio-simple-factory';
import { FormularioDocumentoService } from '../../Envios/documentos/services/formulario-documento.service';
import { DocumentoEnvioSimple, DocumentoEnvioSimpleState } from '../models/documento-envio-simple';
import {
  AjustesEnvioSimple,
  CanalesEnvioSimple,
  crearAjustesEnvioPorDefecto,
  crearCanalesPorDefecto,
  crearMensajeCorreoVacio,
  FirmanteEnvioSimple,
  MensajeCorreoEnvioSimple
} from '../models/firmante-envio-simple';
import { FirmanteRosterItem } from '../models/firmante-roster-item';
import { EnvioSimpleConfirmacionResultado } from '../models/envio-simple-confirmacion.model';
import { EnvioSimpleMiniaturaService } from './envio-simple-miniatura.service';
import { EnvioSimpleBusquedaEmpleadosCacheService } from './envio-simple-busqueda-empleados-cache.service';
import { getFirmanteColorTokens } from '../../Envios/documentos/components/configuracion-panel-firmantes-unificado/firmante-color.tokens';
import {
  ORDEN_CONTACTO_PERSONAL,
  ORDEN_CONTACTO_PROFESIONAL,
  formatearTelefonoContacto,
  normalizarOrigenContacto,
  resolverContactoConFallback
} from '../utils/contacto-origen.util';

@Injectable({
  providedIn: 'root'
})
export class EnvioSimpleFlowService {
  readonly PASOS = ['Documentos', 'Firmantes', 'Envío', 'Confirmación'];

  private indicePasoActivo = 0;
  private documentosSubject = new BehaviorSubject<DocumentoEnvioSimple[]>([]);
  private contadorIds = 0;

  /** Configuracion (FormGroup) por documento, indexada por id de documento. */
  private formulariosPorDocumento = new Map<string, FormGroup>();
  /**
   * Borradores de formularios (casillas) por documento. Viven fuera del
   * FormGroup en `FormularioDocumentoService`, asi que deben conmutarse junto
   * al documento activo para no mezclar nombres entre documentos.
   */
  private borradoresPorDocumento = new Map<string, FormArray>();
  /** Documentos genericos cuya `configuracion` ya se hidrato en su FormGroup. */
  private documentosHidratados = new Set<string>();
  private documentoActivoIdSubject = new BehaviorSubject<string | null>(null);

  /** Tipo de envio seleccionado en el paso previo del flujo Envio simple. */
  tipoEnvio: string | null = null;

  /** Roster global de participantes del envio (principal + adicionales). */
  private firmantesRoster: FirmanteRosterItem[] = [];

  /** Destinatarios del paso Envio (lista unica, envio fusionado — D11). */
  private firmantesEnvio: FirmanteEnvioSimple[] = [];
  private mensajeCorreo: MensajeCorreoEnvioSimple = crearMensajeCorreoVacio();
  private ajustesEnvio: AjustesEnvioSimple = crearAjustesEnvioPorDefecto();
  private canales: CanalesEnvioSimple = crearCanalesPorDefecto();
  private resultadoConfirmacion: EnvioSimpleConfirmacionResultado | null = null;

  documentos$ = this.documentosSubject.asObservable().pipe(
    map(documentos => this.ordenarDocumentos(documentos))
  );

  hayDocumentos$ = this.documentosSubject.asObservable().pipe(
    map(documentos => documentos.length > 0)
  );

  documentoActivoId$ = this.documentoActivoIdSubject.asObservable();

  constructor(
    private miniaturaService: EnvioSimpleMiniaturaService,
    private fb: FormBuilder,
    private formularioDocumentoService: FormularioDocumentoService,
    private busquedaEmpleadosCache: EnvioSimpleBusquedaEmpleadosCacheService
  ) {}

  get pasoActivo(): string {
    return this.PASOS[this.indicePasoActivo];
  }

  get documentos(): DocumentoEnvioSimple[] {
    return this.ordenarDocumentos(this.documentosSubject.value);
  }

  get tieneDocumentos(): boolean {
    return this.documentosSubject.value.length > 0;
  }

  get documentoActivoId(): string | null {
    return this.documentoActivoIdSubject.value;
  }

  get documentoActivo(): DocumentoEnvioSimple | null {
    const id = this.documentoActivoIdSubject.value;
    if (!id) {
      return null;
    }
    return this.documentos.find(doc => doc.id === id) ?? null;
  }

  get formularioDocumentoActivo(): FormGroup | null {
    const id = this.documentoActivoIdSubject.value;
    return id ? this.getFormularioDocumento(id) : null;
  }

  /** Copia del roster global de firmantes del envio. */
  get rosterFirmantes(): FirmanteRosterItem[] {
    return [...this.firmantesRoster];
  }

  /** Copia de los destinatarios del paso Envio. */
  get firmantesEnvioList(): FirmanteEnvioSimple[] {
    return this.firmantesEnvio.map(firmante => ({ ...firmante, color: { ...firmante.color } }));
  }

  get mensajeCorreoEnvio(): MensajeCorreoEnvioSimple {
    return { ...this.mensajeCorreo };
  }

  get ajustesEnvioEnvio(): AjustesEnvioSimple {
    return {
      ...this.ajustesEnvio,
      recordatorios: [...this.ajustesEnvio.recordatorios]
    };
  }

  get canalesEnvio(): CanalesEnvioSimple {
    return { ...this.canales };
  }

  get confirmacionResultado(): EnvioSimpleConfirmacionResultado | null {
    return this.resultadoConfirmacion;
  }

  establecerResultadoConfirmacion(resultado: EnvioSimpleConfirmacionResultado): void {
    this.resultadoConfirmacion = resultado;
  }

  get hayValidadorEnvio(): boolean {
    return this.firmantesEnvio.some(firmante => firmante.esValidador);
  }

  actualizarMensajeCorreo(mensaje: MensajeCorreoEnvioSimple): void {
    this.mensajeCorreo = { ...mensaje };
  }

  actualizarAjustesEnvio(ajustes: AjustesEnvioSimple): void {
    this.ajustesEnvio = {
      ...ajustes,
      recordatorios: [...ajustes.recordatorios]
    };
  }

  actualizarCanales(canales: Partial<CanalesEnvioSimple> | CanalesEnvioSimple): void {
    const previos = this.canales;
    this.canales = { ...this.canales, ...canales };
    const cambioOrigen =
      previos.ordenMail !== this.canales.ordenMail ||
      previos.ordenTelefonoSMS !== this.canales.ordenTelefonoSMS;
    if (cambioOrigen) {
      this.resincronizarContactosFirmantesDesdeEmpleado();
    }
  }

  /**
   * Persiste en memoria el estado capturado en el paso Envio (mensaje, ajustes y canales).
   * Los firmantes del paso se actualizan en tiempo real desde las tarjetas.
   */
  persistirEstadoPasoEnvio(estado: {
    mensajeCorreo: MensajeCorreoEnvioSimple;
    ajustesEnvio: AjustesEnvioSimple;
    canales: Partial<CanalesEnvioSimple> | CanalesEnvioSimple;
  }): void {
    this.actualizarMensajeCorreo(estado.mensajeCorreo);
    this.actualizarAjustesEnvio(estado.ajustesEnvio);
    this.actualizarCanales(estado.canales);
  }

  /**
   * Snapshot del flujo listo para el spec de envio final (sin persistencia backend).
   */
  exportarEstadoPasoEnvio(): ReturnType<EnvioSimpleFlowService['exportarEstadoFlujo']> {
    return this.exportarEstadoFlujo();
  }

  reordenarFirmantesEnvio(previousIndex: number, currentIndex: number): void {
    if (
      previousIndex === currentIndex ||
      previousIndex < 0 ||
      currentIndex < 0 ||
      previousIndex >= this.firmantesEnvio.length ||
      currentIndex >= this.firmantesEnvio.length
    ) {
      return;
    }
    const list = [...this.firmantesEnvio];
    const [moved] = list.splice(previousIndex, 1);
    list.splice(currentIndex, 0, moved);
    this.firmantesEnvio = list.map((firmante, index) => ({ ...firmante, orden: index + 1 }));
  }

  agregarValidadorEnvio(): boolean {
    if (this.hayValidadorEnvio) {
      return false;
    }
    const orden = this.firmantesEnvio.length + 1;
    const nuevoValidador: FirmanteEnvioSimple = {
      codigo: 'VAL-1',
      desc: 'Validador',
      orden,
      esPrincipal: false,
      esValidador: true,
      color: getFirmanteColorTokens('VAL-1'),
      nombre: '',
      apellido1: '',
      apellido2: '',
      email: '',
      telefono: ''
    };
    this.firmantesEnvio = [...this.firmantesEnvio, nuevoValidador];
    return true;
  }

  actualizarDatosFirmanteEnvio(
    codigo: string,
    datos: Partial<Pick<FirmanteEnvioSimple, 'nombre' | 'apellido1' | 'apellido2' | 'email' | 'telefono'>>
  ): void {
    const index = this.firmantesEnvio.findIndex(firmante => firmante.codigo === codigo);
    if (index < 0) {
      return;
    }
    const firmante = this.firmantesEnvio[index];
    const actualizado: FirmanteEnvioSimple = { ...firmante, ...datos };
    if (
      actualizado.datosEmpleado &&
      (datos.email !== undefined || datos.telefono !== undefined)
    ) {
      actualizado.datosEmpleado = this.aplicarContactoInlineADatosEmpleado(
        actualizado.datosEmpleado as EmpleadoCrud,
        {
          email: datos.email !== undefined ? actualizado.email : undefined,
          telefono: datos.telefono !== undefined ? actualizado.telefono : undefined
        }
      );
    }
    this.firmantesEnvio[index] = actualizado;
  }

  aplicarEmpleadoAFirmanteEnvio(codigoFirmante: string, empleado: EmpleadoCrud): void {
    const contactos = this.resolverContactosDesdeEmpleado(empleado);
    this.actualizarDatosFirmanteEnvio(codigoFirmante, {
      nombre: empleado.nombre ?? '',
      apellido1: empleado.apellido1 ?? '',
      apellido2: empleado.apellido2 ?? '',
      email: contactos.email,
      telefono: contactos.telefono
    });
    const index = this.firmantesEnvio.findIndex(firmante => firmante.codigo === codigoFirmante);
    if (index < 0) {
      return;
    }
    const codigoEmpleado = this.resolverCodigoEmpleadoParaFirmante(
      codigoFirmante,
      empleado,
      this.firmantesEnvio[index].codigoEmpleado
    );
    this.firmantesEnvio[index] = {
      ...this.firmantesEnvio[index],
      codigoEmpleado,
      datosEmpleado: {
        ...empleado,
        codigo: codigoEmpleado
      }
    };
  }

  /** Codigo de maestro si existe; si no, codigo temporal estable por firmante (seleccion en lupa). */
  private resolverCodigoEmpleadoParaFirmante(
    codigoFirmante: string,
    empleado: EmpleadoCrud,
    codigoPrevio?: string
  ): string {
    const codigo = empleado?.codigo != null ? String(empleado.codigo).trim() : '';
    if (codigo) {
      return codigo;
    }
    const previo = codigoPrevio != null ? String(codigoPrevio).trim() : '';
    if (previo) {
      return previo;
    }
    return `TMP-${codigoFirmante}`;
  }

  /** Reaplica email/telefono inline desde datosEmpleado segun ordenMail/ordenTelefonoSMS. */
  resincronizarContactosFirmantesDesdeEmpleado(): void {
    this.firmantesEnvio = this.firmantesEnvio.map(firmante => {
      if (!firmante.datosEmpleado) {
        return firmante;
      }
      const contactos = this.resolverContactosDesdeEmpleado(firmante.datosEmpleado as EmpleadoCrud);
      return {
        ...firmante,
        email: contactos.email,
        telefono: contactos.telefono
      };
    });
  }

  construirEmpleadoDesdeFirmanteEnvio(firmante: FirmanteEnvioSimple): EmpleadoCrud {
    const base = firmante.datosEmpleado
      ? { ...(firmante.datosEmpleado as EmpleadoCrud) }
      : {
          codigo: firmante.codigoEmpleado ?? '',
          tipoDocumento: '',
          nif: '',
          fechaNacimiento: '',
          nombre: '',
          apellido1: '',
          apellido2: '',
          nacionalidad: '',
          lugarNacimiento: '',
          estadoCivil: '',
          sexo: '',
          tipoPersona: '',
          sigla: '',
          provincia: '',
          municipio: '',
          codigoPostal: '',
          numero: '',
          escalera: '',
          piso: '',
          puerta: '',
          mailPersonal: '',
          extensionTelefonoPersonal: '',
          telefonoPersonal: '',
          mailProfesional: '',
          extensionTelefonoProfesional: '',
          telefonoProfesional: '',
          fechaAlta: '',
          puestoTrabajo: '',
          categoria: '',
          convenio: '',
          codigoEmpresa: '',
          codigoCentro: '',
          empresaCif: '',
          nombreEmpresa: '',
          nombreCentro: '',
          codigoExterno: ''
        };

    // Inline de tarjeta: sincroniza el campo del origen preferido; el resto del snapshot se conserva
    const origenMail = normalizarOrigenContacto(this.canales.ordenMail);
    const origenTelefono = normalizarOrigenContacto(this.canales.ordenTelefonoSMS);
    const telefonoInlinePersonal =
      origenTelefono === ORDEN_CONTACTO_PERSONAL ? firmante.telefono ?? base.telefonoPersonal ?? '' : undefined;
    const telefonoInlineProfesional =
      origenTelefono === ORDEN_CONTACTO_PROFESIONAL
        ? firmante.telefono ?? base.telefonoProfesional ?? ''
        : undefined;
    return {
      ...base,
      codigo: firmante.codigoEmpleado || base.codigo || '',
      nombre: firmante.nombre ?? base.nombre ?? '',
      apellido1: firmante.apellido1 ?? base.apellido1 ?? '',
      apellido2: firmante.apellido2 ?? base.apellido2 ?? '',
      mailPersonal:
        origenMail === ORDEN_CONTACTO_PERSONAL
          ? firmante.email ?? base.mailPersonal ?? ''
          : base.mailPersonal ?? '',
      mailProfesional:
        origenMail === ORDEN_CONTACTO_PROFESIONAL
          ? firmante.email ?? base.mailProfesional ?? ''
          : base.mailProfesional ?? '',
      telefonoPersonal:
        telefonoInlinePersonal !== undefined ? telefonoInlinePersonal : base.telefonoPersonal ?? '',
      extensionTelefonoPersonal:
        telefonoInlinePersonal !== undefined ? '' : base.extensionTelefonoPersonal ?? '',
      telefonoProfesional:
        telefonoInlineProfesional !== undefined ? telefonoInlineProfesional : base.telefonoProfesional ?? '',
      extensionTelefonoProfesional:
        telefonoInlineProfesional !== undefined ? '' : base.extensionTelefonoProfesional ?? ''
    };
  }

  /** Propaga email/telefono inline al snapshot de empleado del origen activo. */
  private aplicarContactoInlineADatosEmpleado(
    empleado: EmpleadoCrud,
    contacto: { email?: string; telefono?: string }
  ): EmpleadoCrud {
    const origenMail = normalizarOrigenContacto(this.canales.ordenMail);
    const origenTelefono = normalizarOrigenContacto(this.canales.ordenTelefonoSMS);
    const actualizado = { ...empleado };

    if (contacto.email !== undefined) {
      if (origenMail === ORDEN_CONTACTO_PERSONAL) {
        actualizado.mailPersonal = contacto.email;
      } else {
        actualizado.mailProfesional = contacto.email;
      }
    }

    if (contacto.telefono !== undefined) {
      if (origenTelefono === ORDEN_CONTACTO_PERSONAL) {
        actualizado.telefonoPersonal = contacto.telefono;
        actualizado.extensionTelefonoPersonal = '';
      } else {
        actualizado.telefonoProfesional = contacto.telefono;
        actualizado.extensionTelefonoProfesional = '';
      }
    }

    return actualizado;
  }

  /** Propaga tipos de contenido del primer documento al resto (envio fusionado). */
  propagarTiposContenidoPrincipalATodosLosDocumentos(): void {
    const documentos = this.documentos;
    if (documentos.length < 2) {
      return;
    }
    const origen = this.getFormularioDocumento(documentos[0].id);
    const tiposContenido = (origen.get('tiposContenido') as FormArray | null)?.value ?? [];
    documentos.slice(1).forEach(documento => {
      const destino = this.getFormularioDocumento(documento.id);
      destino.setControl(
        'tiposContenido',
        this.formularioDocumentoService.buildPalabrasFormArray(tiposContenido)
      );
      destino.get('incluirContenido')?.setValue((tiposContenido as unknown[]).length > 0, { emitEvent: false });
    });
  }

  inicializar(): void {
    this.indicePasoActivo = 0;
    this.contadorIds = 0;
    this.tipoEnvio = null;
    this.formulariosPorDocumento.clear();
    this.borradoresPorDocumento.clear();
    this.documentosHidratados.clear();
    this.firmantesRoster = [];
    this.reiniciarEstadoPasoEnvio();
    this.busquedaEmpleadosCache.limpiar();
    this.documentoActivoIdSubject.next(null);
    this.documentosSubject.next([]);
  }

  establecerPaso(indice: number): void {
    if (indice >= 0 && indice < this.PASOS.length) {
      this.indicePasoActivo = indice;
    }
  }

  agregarDocumento(documento: Omit<DocumentoEnvioSimple, 'id' | 'orden'>): DocumentoEnvioSimple {
    const nuevoDocumento: DocumentoEnvioSimple = {
      ...documento,
      id: this.generarId(),
      orden: this.documentos.length
    };
    this.documentosSubject.next([...this.documentosSubject.value, nuevoDocumento]);
    this.generarMiniaturaSiAplica(nuevoDocumento);

    // Sin documento activo (p. ej. el usuario borro todos en Firmantes): activar
    // el recien anadido para que reaparezcan preview y panel con firmante principal.
    if (!this.documentoActivoIdSubject.value) {
      this.asegurarFormularioDocumento(nuevoDocumento);
      this.documentoActivoIdSubject.next(nuevoDocumento.id);
    }

    if (this.indicePasoActivo === 1 && this.firmantesRoster.length > 0) {
      this.asegurarFormularioDocumento(nuevoDocumento);
      this.propagarRosterADocumento(nuevoDocumento.id);
    }

    return nuevoDocumento;
  }

  reordenarDocumentos(documentos: DocumentoEnvioSimple[]): void {
    const reordenados = documentos.map((documento, indice) => ({
      ...documento,
      orden: indice
    }));
    this.documentosSubject.next(reordenados);
  }

  eliminarDocumento(id: string): void {
    const actualizados = this.documentosSubject.value
      .filter(doc => doc.id !== id)
      .map((doc, idx) => ({ ...doc, orden: idx }));
    this.documentosSubject.next(actualizados);

    this.formulariosPorDocumento.delete(id);
    this.borradoresPorDocumento.delete(id);
    this.documentosHidratados.delete(id);
    if (this.documentoActivoIdSubject.value === id) {
      const siguienteActivo = this.ordenarDocumentos(actualizados)[0]?.id ?? null;
      this.documentoActivoIdSubject.next(siguienteActivo);
    }
  }

  /**
   * Prepara el store para el paso Firmantes: garantiza un `FormGroup` por
   * documento y fija el documento activo (el primero si aun no hay ninguno).
   */
  prepararPasoFirmantes(): void {
    const documentos = this.documentos;
    const idsVigentes = new Set(documentos.map(doc => doc.id));

    documentos.forEach(documento => this.asegurarFormularioDocumento(documento));

    Array.from(this.formulariosPorDocumento.keys())
      .filter(id => !idsVigentes.has(id))
      .forEach(id => this.formulariosPorDocumento.delete(id));

    Array.from(this.borradoresPorDocumento.keys())
      .filter(id => !idsVigentes.has(id))
      .forEach(id => this.borradoresPorDocumento.delete(id));

    const activoActual = this.documentoActivoIdSubject.value;
    if (!activoActual || !idsVigentes.has(activoActual)) {
      this.documentoActivoIdSubject.next(documentos[0]?.id ?? null);
    }

    this.sincronizarRosterDesdeDocumentos();
  }

  /** Devuelve el `FormGroup` del documento, creandolo vacio si no existe. */
  getFormularioDocumento(id: string): FormGroup {
    let formulario = this.formulariosPorDocumento.get(id);
    if (!formulario) {
      formulario = this.crearFormularioParaDocumento(id);
      this.formulariosPorDocumento.set(id, formulario);
    }
    return formulario;
  }

  /** Guarda (snapshot) el `FormGroup` editado de un documento en el store. */
  guardarFormularioDocumento(id: string, formulario: FormGroup): void {
    this.formulariosPorDocumento.set(id, formulario);
  }

  /** Devuelve el borrador de formularios guardado de un documento, si existe. */
  getBorradoresFormulario(id: string): FormArray | undefined {
    return this.borradoresPorDocumento.get(id);
  }

  /** Guarda (por referencia) el borrador de formularios del documento. */
  guardarBorradoresFormulario(id: string, borradores: FormArray): void {
    this.borradoresPorDocumento.set(id, borradores);
  }

  /**
   * Indica si un documento generico requiere hidratar su `configuracion` en el
   * FormGroup (fase 5b.1): tiene origen generico, trae configuracion y aun no se
   * ha hidratado. Asi se evita re-hidratar y perder ediciones del usuario.
   */
  requiereHidratacionGenerico(id: string): boolean {
    const documento = this.documentos.find(doc => doc.id === id);
    return (
      !!documento &&
      documento.origen === 'generico' &&
      !!documento.configuracion &&
      !this.documentosHidratados.has(id)
    );
  }

  /** Marca el documento generico como ya hidratado en su FormGroup. */
  marcarDocumentoHidratado(id: string): void {
    this.documentosHidratados.add(id);
  }

  /**
   * Indica si el documento tiene gadgets configurados (firmas colocadas,
   * casillas/formularios, campos editables o tipos de contenido). Se usa para
   * decidir si pedir confirmacion al quitar el documento (fase 6.1). Lee el
   * FormGroup ya existente sin crearlo: un documento sin FormGroup no tiene
   * gadgets.
   */
  documentoTieneGadgets(id: string): boolean {
    const formulario = this.formulariosPorDocumento.get(id);
    if (!formulario) {
      return false;
    }

    const tiposContenido = formulario.get('tiposContenido') as FormArray | null;
    if ((tiposContenido?.length ?? 0) > 0) {
      return true;
    }

    const firmantesNif = formulario.get('firmantesNif') as FormArray | null;
    if (!firmantesNif) {
      return false;
    }

    return firmantesNif.controls.some(ctrl => {
      const firmante = ctrl as FormGroup;
      const posicionFirmas = firmante.get('firmas')?.get('posicionFirmas') as FormArray | null;
      const formularios = firmante.get('formularios') as FormArray | null;
      const camposEditables = firmante.get('camposEditables') as FormArray | null;
      return (
        (posicionFirmas?.length ?? 0) > 0 ||
        (formularios?.length ?? 0) > 0 ||
        (camposEditables?.length ?? 0) > 0
      );
    });
  }

  /**
   * Cambia el documento activo sincronizando el flujo: guarda el snapshot del
   * documento saliente (si se aporta su `FormGroup`) y deja el entrante como
   * activo. El consumidor puede leer `formularioDocumentoActivo` para cargarlo.
   */
  seleccionarDocumentoActivo(id: string, formularioSaliente?: FormGroup): void {
    if (!this.documentos.some(doc => doc.id === id)) {
      return;
    }

    const salienteId = this.documentoActivoIdSubject.value;
    if (formularioSaliente && salienteId && salienteId !== id) {
      this.guardarFormularioDocumento(salienteId, formularioSaliente);
    }

    this.asegurarFormularioDocumento(this.documentos.find(doc => doc.id === id));
    this.documentoActivoIdSubject.next(id);
  }

  /** Estado completo (metadatos + FormGroup) de un documento del flujo. */
  obtenerEstadoDocumento(id: string): DocumentoEnvioSimpleState | null {
    const documento = this.documentos.find(doc => doc.id === id);
    if (!documento) {
      return null;
    }
    return {
      documento,
      formularioDocumento: this.getFormularioDocumento(id)
    };
  }

  /** Estado completo del flujo en orden de envio, para specs posteriores. */
  obtenerEstados(): DocumentoEnvioSimpleState[] {
    return this.documentos.map(documento => ({
      documento,
      formularioDocumento: this.getFormularioDocumento(documento.id)
    }));
  }

  /**
   * Snapshot completo del flujo (tipo de envio + documentos con su FormGroup)
   * para entregar al paso Envio / specs posteriores (D7/D8). No persiste en
   * backend: solo expone el estado front acumulado.
   */
  exportarEstadoFlujo(): {
    tipoEnvio: string | null;
    firmantesRoster: FirmanteRosterItem[];
    firmantesEnvio: FirmanteEnvioSimple[];
    mensajeCorreo: MensajeCorreoEnvioSimple;
    ajustesEnvio: AjustesEnvioSimple;
    canales: CanalesEnvioSimple;
    documentos: DocumentoEnvioSimpleState[];
  } {
    return {
      tipoEnvio: this.tipoEnvio,
      firmantesRoster: this.rosterFirmantes,
      firmantesEnvio: this.firmantesEnvioList,
      mensajeCorreo: this.mensajeCorreoEnvio,
      ajustesEnvio: this.ajustesEnvioEnvio,
      canales: this.canalesEnvio,
      documentos: this.obtenerEstados()
    };
  }

  /**
   * Prepara el paso Envio: hidrata `firmantesEnvio` desde el roster global
   * (lista unica para envio fusionado, sin reconciliacion por documento — D11).
   * Conserva datos de contacto ya capturados y validadores anadidos.
   */
  prepararPasoEnvio(): void {
    const roster = this.rosterFirmantes;
    const existentes = new Map(this.firmantesEnvio.map(firmante => [firmante.codigo, firmante]));
    const validadores = this.firmantesEnvio.filter(firmante => firmante.esValidador);

    const desdeRoster = roster.map((item, index) => {
      const previo = existentes.get(item.codigo);
      const color = getFirmanteColorTokens(item.codigo);
      const esPrincipal = item.codigo === CODIGO_FIRMANTE_PRINCIPAL;
      if (previo && !previo.esValidador) {
        return {
          ...previo,
          desc: item.desc,
          orden: index + 1,
          esPrincipal,
          color
        };
      }
      return this.crearFirmanteEnvioDesdeRoster(item, index + 1, color, esPrincipal);
    });

    const ordenBase = desdeRoster.length;
    const validadoresRenumerados = validadores.map((validador, index) => ({
      ...validador,
      orden: ordenBase + index + 1
    }));

    this.firmantesEnvio = [...desdeRoster, ...validadoresRenumerados];
  }

  /**
   * Construye el roster como union de firmantes presentes en todos los documentos
   * (FormGroup hidratado y/o `configuracion` importada de genericos) y propaga
   * entradas vacias donde falten (D5).
   */
  sincronizarRosterDesdeDocumentos(): void {
    const codigos = new Set<string>();

    this.documentos.forEach(documento => {
      this.sanitizarFirmantesImportadosEnFormulario(documento.id);
      this.expandirCodigosDuplicadosEnDocumento(documento);
      this.recogerCodigosFirmantesDocumento(documento, codigos);
    });

    this.asegurarCodigoPrincipalEnUnion(codigos);

    this.renumerarRosterDesdeCodigos(Array.from(codigos));
    this.propagarRosterATodosLosDocumentos();
  }

  /** Anade un firmante adicional al roster y a todos los documentos. Devuelve su codigo. */
  agregarFirmanteGlobal(): string | null {
    if (this.firmantesRoster.length >= MAX_FIRMANTES_ENVIO_SIMPLE) {
      return null;
    }
    const usados = new Set(this.firmantesRoster.map(item => Number(item.codigo)));
    let codigo = 101;
    while (usados.has(codigo)) {
      codigo++;
    }
    const codigoStr = String(codigo);
    this.firmantesRoster.push({ codigo: codigoStr, desc: 'Firmante' });
    this.renumerarRoster();
    this.propagarRosterATodosLosDocumentos();
    return codigoStr;
  }

  /** Elimina un firmante adicional del roster y de todos los documentos. */
  eliminarFirmanteGlobal(codigo: string): void {
    if (codigo === CODIGO_FIRMANTE_PRINCIPAL) {
      return;
    }
    this.firmantesRoster = this.firmantesRoster.filter(item => item.codigo !== codigo);
    this.renumerarRoster();
    this.documentos.forEach(documento => {
      const formulario = this.getFormularioDocumento(documento.id);
      const firmantes = (formulario.get('firmantesNif') as FormArray).value as FirmanteNif[];
      const restantes = firmantes.filter(f => f.tipoFirmante?.codigo !== codigo);
      this.actualizarFirmantesEnFormulario(formulario, restantes);
      this.limpiarBorradoresFirmanteEnDocumento(documento.id, codigo);
    });
    this.firmantesEnvio = this.firmantesEnvio
      .filter(firmante => firmante.codigo !== codigo)
      .map((firmante, index) => ({ ...firmante, orden: index + 1 }));
  }

  /**
   * Resetea la configuracion de un firmante solo en un documento (D4 limpiar local).
   * El firmante permanece en el roster y en el resto de documentos.
   */
  limpiarFirmanteEnDocumento(codigo: string, documentoId: string): void {
    const formulario = this.getFormularioDocumento(documentoId);
    const rosterItem = this.firmantesRoster.find(item => item.codigo === codigo);
    const firmantes = (formulario.get('firmantesNif') as FormArray).value as FirmanteNif[];
    const desc =
      rosterItem?.desc ??
      firmantes.find(f => f.tipoFirmante?.codigo === codigo)?.tipoFirmante?.desc ??
      'Firmante';
    const nuevos = firmantes.map(firmante =>
      firmante.tipoFirmante?.codigo === codigo ? construirFirmanteVacio(codigo, desc) : firmante
    );
    this.actualizarFirmantesEnFormulario(formulario, nuevos);

    if (codigo === CODIGO_FIRMANTE_PRINCIPAL) {
      formulario.setControl('tiposContenido', this.fb.array([]));
      formulario.get('incluirContenido')?.setValue(false, { emitEvent: false });
    }
    this.limpiarBorradoresFirmanteEnDocumento(documentoId, codigo);
  }

  firmanteTieneConfiguracionEnDocumento(codigo: string, documentoId: string): boolean {
    const formulario = this.formulariosPorDocumento.get(documentoId);
    if (!formulario) {
      return false;
    }

    if (codigo === CODIGO_FIRMANTE_PRINCIPAL) {
      const tiposContenido = formulario.get('tiposContenido') as FormArray | null;
      if ((tiposContenido?.length ?? 0) > 0) {
        return true;
      }
    }

    const firmantesNif = formulario.get('firmantesNif') as FormArray | null;
    const firmante = firmantesNif?.controls.find(
      ctrl => (ctrl as FormGroup).get('tipoFirmante')?.value?.codigo === codigo
    ) as FormGroup | undefined;
    if (!firmante) {
      return false;
    }

    const firmas = firmante.get('firmas');
    const tipo = firmas?.get('tipoFirma')?.value;
    if (tipo === TipoFirma.Dibujada || tipo === TipoFirma.Certificado) {
      if (firmas?.get('tieneTag')?.value === true) {
        const nombreTag = firmas.get('nombreTag')?.value as string;
        if (nombreTag?.trim()) {
          return true;
        }
      }
      const posicionFirmas = firmas?.get('posicionFirmas') as FormArray | null;
      if ((posicionFirmas?.length ?? 0) > 0) {
        return true;
      }
    }

    const formularios = firmante.get('formularios') as FormArray | null;
    const camposEditables = firmante.get('camposEditables') as FormArray | null;
    return (formularios?.length ?? 0) > 0 || (camposEditables?.length ?? 0) > 0;
  }

  obtenerDocumentosConConfiguracionFirmante(codigo: string): DocumentoEnvioSimple[] {
    return this.documentos.filter(documento =>
      this.firmanteTieneConfiguracionEnDocumento(codigo, documento.id)
    );
  }

  /** Firmantes del roster sin configuracion en ningun documento (excluye al principal). */
  obtenerFirmantesRosterSinConfiguracion(): FirmanteRosterItem[] {
    return this.rosterFirmantes.filter(
      item =>
        item.codigo !== CODIGO_FIRMANTE_PRINCIPAL &&
        this.obtenerDocumentosConConfiguracionFirmante(item.codigo).length === 0
    );
  }

  /** Elimina del roster y de todos los documentos a los firmantes sin configuracion. */
  eliminarFirmantesRosterSinConfiguracion(): void {
    this.obtenerFirmantesRosterSinConfiguracion().forEach(item =>
      this.eliminarFirmanteGlobal(item.codigo)
    );
  }

  /**
   * Regla de flujo (D6): true si AL MENOS UN documento tiene el firmante
   * principal con una firma completa: tipo de firma con firma (Dibujada/
   * Certificado) y, o bien una firma colocada (`posicionFirmas`), o bien firma
   * por tag con `nombreTag` valido. No exige que todos los documentos la cumplan.
   */
  algunDocumentoConFirmaPrincipalCompleta(): boolean {
    return this.documentos.some(doc =>
      this.principalTieneFirmaConfigurada(this.getFormularioDocumento(doc.id))
    );
  }

  private principalTieneFirmaConfigurada(formulario: FormGroup): boolean {
    const firmantesNif = formulario.get('firmantesNif') as FormArray | null;
    const principal = firmantesNif?.controls.find(
      ctrl => (ctrl as FormGroup).get('tipoFirmante')?.value?.codigo === CODIGO_FIRMANTE_PRINCIPAL
    ) as FormGroup | undefined;
    const firmas = principal?.get('firmas') as FormGroup | null;
    if (!firmas) {
      return false;
    }
    const tipo = firmas.get('tipoFirma')?.value;
    if (tipo !== TipoFirma.Dibujada && tipo !== TipoFirma.Certificado) {
      return false;
    }
    const tieneTag = firmas.get('tieneTag')?.value;
    if (tieneTag === true) {
      const nombreTag = firmas.get('nombreTag')?.value as string;
      return !!nombreTag && /^\S*$/.test(nombreTag) && nombreTag.length <= 200;
    }
    const posicionFirmas = firmas.get('posicionFirmas') as FormArray | null;
    return (posicionFirmas?.length ?? 0) > 0;
  }

  private asegurarFormularioDocumento(documento?: DocumentoEnvioSimple): void {
    if (!documento || this.formulariosPorDocumento.has(documento.id)) {
      return;
    }
    this.formulariosPorDocumento.set(documento.id, this.crearFormularioParaDocumento(documento.id));
  }

  private crearFormularioParaDocumento(id: string): FormGroup {
    const formulario = buildFormularioDocumentoVacio(this.fb);
    const documento = this.documentos.find(doc => doc.id === id);
    if (documento) {
      formulario.patchValue({
        nombre: documento.nombre ?? '',
        generico: documento.generico ?? null,
        idTipoDocumento: documento.idTipoDocumento ?? null
      });
    }
    return formulario;
  }

  private generarMiniaturaSiAplica(documento: DocumentoEnvioSimple): void {
    const fuente: Blob | undefined = documento.archivo ?? documento.pdfBlob;
    if (!fuente) {
      return;
    }
    // Los genericos vienen de fetch(urlPdf) sin mime fiable; los subidos deben ser PDF.
    if (!documento.pdfBlob && fuente.type !== 'application/pdf') {
      return;
    }

    this.miniaturaService.generarDesdeArchivo(fuente)
      .then(miniatura => this.actualizarMiniatura(documento.id, miniatura))
      .catch(() => undefined);
  }

  private actualizarMiniatura(id: string, miniatura: string): void {
    const actualizados = this.documentosSubject.value.map(doc =>
      doc.id === id ? { ...doc, miniatura } : doc
    );
    this.documentosSubject.next(actualizados);
  }

  private ordenarDocumentos(documentos: DocumentoEnvioSimple[]): DocumentoEnvioSimple[] {
    return [...documentos].sort((a, b) => a.orden - b.orden);
  }

  private generarId(): string {
    this.contadorIds += 1;
    return `envio-simple-doc-${this.contadorIds}`;
  }

  private propagarRosterATodosLosDocumentos(): void {
    this.documentos.forEach(documento => this.propagarRosterADocumento(documento.id));
  }

  /**
   * Si la union no incluye `100`, o bien se crea vacio (sin firmantes) o se
   * promueve el codigo mas bajo presente a principal, conservando su configuracion
   * (evita "Firmante 2" + principal vacio con aspa al importar solo adicionales).
   */
  private asegurarCodigoPrincipalEnUnion(codigos: Set<string>): void {
    if (codigos.has(CODIGO_FIRMANTE_PRINCIPAL)) {
      return;
    }

    if (codigos.size === 0) {
      codigos.add(CODIGO_FIRMANTE_PRINCIPAL);
      return;
    }

    const origen = [...codigos].sort((a, b) => Number(a) - Number(b))[0];
    this.documentos.forEach(documento =>
      this.remapearCodigoFirmanteEnDocumento(documento, origen, CODIGO_FIRMANTE_PRINCIPAL)
    );
    codigos.delete(origen);
    codigos.add(CODIGO_FIRMANTE_PRINCIPAL);
  }

  /**
   * Genericos legacy (p.ej. actas masivas) pueden repetir el mismo `tipoFirmante.codigo`
   * en varias filas y distinguirse solo por `orden`/`nifOrden`. Envio simple exige
   * codigo unico por firmante: se reasignan 100, 101, 102… por orden.
   */
  private expandirCodigosDuplicadosEnDocumento(documento: DocumentoEnvioSimple): void {
    const formulario = this.formulariosPorDocumento.get(documento.id);
    if (formulario) {
      const firmantes = ((formulario.get('firmantesNif') as FormArray | null)?.value ??
        []) as FirmanteNif[];
      if (this.tieneCodigosFirmanteDuplicados(firmantes)) {
        this.actualizarFirmantesEnFormulario(
          formulario,
          this.asignarCodigosUnicosPorOrden(firmantes)
        );
      }
    }

    const firmantesConfig = documento.configuracion?.firmantesNif as FirmanteNif[] | undefined;
    if (!firmantesConfig?.length || !this.tieneCodigosFirmanteDuplicados(firmantesConfig)) {
      return;
    }

    const remapeados = this.asignarCodigosUnicosPorOrden(firmantesConfig);
    documento.configuracion = {
      ...documento.configuracion,
      firmantesNif: remapeados
    };
    const actualizados = this.documentosSubject.value.map(doc =>
      doc.id === documento.id ? { ...doc, configuracion: documento.configuracion } : doc
    );
    this.documentosSubject.next(actualizados);
  }

  private tieneCodigosFirmanteDuplicados(firmantes: FirmanteNif[]): boolean {
    const vistos = new Set<string>();
    for (const firmante of firmantes) {
      const codigo = firmante.tipoFirmante?.codigo;
      if (!codigo) {
        continue;
      }
      if (vistos.has(codigo)) {
        return true;
      }
      vistos.add(codigo);
    }
    return false;
  }

  /** Asigna codigos 100..n y etiquetas Envio simple segun `orden` / `nifOrden`. */
  private asignarCodigosUnicosPorOrden(firmantes: FirmanteNif[]): FirmanteNif[] {
    const ordenados = firmantes
      .map((firmante, index) => ({ firmante, index }))
      .sort((a, b) => {
        const ordenA = this.ordenFirmanteImportado(a.firmante);
        const ordenB = this.ordenFirmanteImportado(b.firmante);
        if (ordenA !== ordenB) {
          return ordenA - ordenB;
        }
        return a.index - b.index;
      })
      .slice(0, MAX_FIRMANTES_ENVIO_SIMPLE);

    let posicionEtiqueta = 2;
    return ordenados.map(({ firmante }, offset) => {
      const codigo = String(Number(CODIGO_FIRMANTE_PRINCIPAL) + offset);
      const desc =
        codigo === CODIGO_FIRMANTE_PRINCIPAL ? 'Firmante principal' : `Firmante ${posicionEtiqueta++}`;
      return {
        ...firmante,
        tipoFirmante: {
          ...firmante.tipoFirmante,
          codigo,
          desc
        }
      };
    });
  }

  private ordenFirmanteImportado(firmante: FirmanteNif): number {
    const orden = firmante.orden;
    if (orden != null && Number.isFinite(Number(orden)) && Number(orden) > 0) {
      return Number(orden);
    }
    const nifOrden = firmante.nifOrden;
    if (nifOrden != null && Number.isFinite(Number(nifOrden)) && Number(nifOrden) > 0) {
      return Number(nifOrden);
    }
    return Number.MAX_SAFE_INTEGER;
  }

  /** Remapea `desde` → `hasta` en FormGroup, borradores y `configuracion` del generico. */
  private remapearCodigoFirmanteEnDocumento(
    documento: DocumentoEnvioSimple,
    desde: string,
    hasta: string
  ): void {
    const formulario = this.formulariosPorDocumento.get(documento.id);
    if (formulario) {
      const firmantes = ((formulario.get('firmantesNif') as FormArray | null)?.value ??
        []) as FirmanteNif[];
      if (firmantes.some(f => f.tipoFirmante?.codigo === desde)) {
        const remapeados = firmantes.map(firmante =>
          firmante.tipoFirmante?.codigo === desde
            ? {
                ...firmante,
                tipoFirmante: {
                  ...firmante.tipoFirmante,
                  codigo: hasta,
                  desc: 'Firmante principal'
                }
              }
            : firmante
        );
        this.actualizarFirmantesEnFormulario(formulario, remapeados);
      }
    }

    this.remapearCodigoEnBorradores(documento.id, desde, hasta);

    const firmantesConfig = documento.configuracion?.firmantesNif as FirmanteNif[] | undefined;
    if (!firmantesConfig?.length) {
      return;
    }

    const configRemapeada = firmantesConfig.map(firmante =>
      firmante.tipoFirmante?.codigo === desde
        ? {
            ...firmante,
            tipoFirmante: {
              ...firmante.tipoFirmante,
              codigo: hasta,
              desc: 'Firmante principal'
            }
          }
        : firmante
    );
    const huboCambio = configRemapeada.some(
      (firmante, index) => firmante.tipoFirmante?.codigo !== firmantesConfig[index]?.tipoFirmante?.codigo
    );
    if (!huboCambio) {
      return;
    }

    documento.configuracion = {
      ...documento.configuracion,
      firmantesNif: configRemapeada
    };
    const actualizados = this.documentosSubject.value.map(doc =>
      doc.id === documento.id ? { ...doc, configuracion: documento.configuracion } : doc
    );
    this.documentosSubject.next(actualizados);
  }

  private remapearCodigoEnBorradores(documentoId: string, desde: string, hasta: string): void {
    const borradores = this.borradoresPorDocumento.get(documentoId);
    if (!borradores) {
      return;
    }
    borradores.controls.forEach(ctrl => {
      const row = ctrl as FormGroup;
      const firmante = row.get('firmante')?.value as { codigo?: string; desc?: string } | null;
      if (firmante?.codigo !== desde) {
        return;
      }
      row.get('firmante')?.setValue(
        { ...firmante, codigo: hasta, desc: 'Firmante principal' },
        { emitEvent: false }
      );
    });
  }

  /**
   * Union de codigos de firmantes de un documento: estado en FormGroup (si existe)
   * mas firmantes declarados en `configuracion` de genericos aun no hidratados.
   */
  private recogerCodigosFirmantesDocumento(documento: DocumentoEnvioSimple, codigos: Set<string>): void {
    const formulario = this.formulariosPorDocumento.get(documento.id);
    const tiposContenidoFormulario =
      ((formulario?.get('tiposContenido') as FormArray | null)?.value as unknown[] | undefined) ?? [];
    const firmantesFormulario = formulario
      ? ((formulario.get('firmantesNif') as FormArray | null)?.value as FirmanteNif[] ?? [])
      : [];
    this.sanitizarFirmantesImportados(
      firmantesFormulario,
      tiposContenidoFormulario.length > 0
    ).forEach(firmante => {
      const codigo = firmante.tipoFirmante?.codigo;
      if (codigo) {
        codigos.add(codigo);
      }
    });

    const tiposContenidoConfiguracion =
      ((documento.configuracion?.tiposContenido as unknown[] | undefined) ?? []);
    const firmantesConfiguracion = (documento.configuracion?.firmantesNif ?? []) as FirmanteNif[];
    this.sanitizarFirmantesImportados(
      firmantesConfiguracion,
      tiposContenidoConfiguracion.length > 0
    ).forEach(firmante => {
      const codigo = firmante.tipoFirmante?.codigo;
      if (codigo) {
        codigos.add(codigo);
      }
    });
  }

  private propagarRosterADocumento(documentoId: string): void {
    if (this.firmantesRoster.length === 0) {
      return;
    }
    const formulario = this.getFormularioDocumento(documentoId);
    const firmantesArray = formulario.get('firmantesNif') as FormArray;

    this.firmantesRoster.forEach(item => {
      const existente = firmantesArray.controls.find(
        ctrl => (ctrl as FormGroup).get('tipoFirmante')?.value?.codigo === item.codigo
      ) as FormGroup | undefined;

      if (existente) {
        existente.get('tipoFirmante')?.get('desc')?.setValue(item.desc, { emitEvent: false });
        return;
      }

      this.formularioDocumentoService.appendFirmanteVacioEnFormulario(
        formulario,
        item.codigo,
        item.desc
      );
    });
  }

  private actualizarFirmantesEnFormulario(formulario: FormGroup, firmantes: FirmanteNif[]): void {
    const renumerados = this.renumerarEtiquetasFirmantes(firmantes);
    this.formularioDocumentoService.actualizarFirmantesNifEnFormulario(formulario, renumerados);
  }

  private renumerarRosterDesdeCodigos(codigos: string[]): void {
    const ordenados = [...codigos].sort((a, b) => Number(a) - Number(b));
    let posicion = 2;
    this.firmantesRoster = ordenados.map(codigo => ({
      codigo,
      desc: codigo === CODIGO_FIRMANTE_PRINCIPAL ? 'Firmante principal' : `Firmante ${posicion++}`
    }));
  }

  private renumerarRoster(): void {
    this.renumerarRosterDesdeCodigos(this.firmantesRoster.map(item => item.codigo));
  }

  private renumerarEtiquetasFirmantes(firmantes: FirmanteNif[]): FirmanteNif[] {
    const ordenados = [...firmantes].sort(
      (a, b) => Number(a.tipoFirmante?.codigo) - Number(b.tipoFirmante?.codigo)
    );
    let posicion = 2;
    return ordenados.map(firmante => {
      const codigo = firmante.tipoFirmante?.codigo;
      const desc =
        codigo === CODIGO_FIRMANTE_PRINCIPAL ? 'Firmante principal' : `Firmante ${posicion++}`;
      return {
        ...firmante,
        tipoFirmante: { ...firmante.tipoFirmante, desc }
      };
    });
  }

  private limpiarBorradoresFirmanteEnDocumento(documentoId: string, codigo: string): void {
    const borradores = this.borradoresPorDocumento.get(documentoId);
    if (!borradores) {
      return;
    }
    for (let i = borradores.length - 1; i >= 0; i--) {
      const row = borradores.at(i) as FormGroup;
      const firmante = row.get('firmante')?.value as { codigo?: string } | null;
      if (firmante?.codigo === codigo) {
        borradores.removeAt(i);
      }
    }
  }

  private reiniciarEstadoPasoEnvio(): void {
    this.firmantesEnvio = [];
    this.mensajeCorreo = crearMensajeCorreoVacio();
    this.ajustesEnvio = crearAjustesEnvioPorDefecto();
    this.canales = crearCanalesPorDefecto();
    this.resultadoConfirmacion = null;
  }

  private crearFirmanteEnvioDesdeRoster(
    item: FirmanteRosterItem,
    orden: number,
    color: ReturnType<typeof getFirmanteColorTokens>,
    esPrincipal: boolean
  ): FirmanteEnvioSimple {
    return {
      codigo: item.codigo,
      desc: item.desc,
      orden,
      esPrincipal,
      esValidador: false,
      color,
      nombre: '',
      apellido1: '',
      apellido2: '',
      email: '',
      telefono: ''
    };
  }

  private resolverContactosDesdeEmpleado(empleado: EmpleadoCrud): { email: string; telefono: string } {
    const email = resolverContactoConFallback(
      this.canales.ordenMail,
      empleado.mailPersonal,
      empleado.mailProfesional
    );
    const telefonoPersonal = formatearTelefonoContacto(
      empleado.extensionTelefonoPersonal,
      empleado.telefonoPersonal
    );
    const telefonoProfesional = formatearTelefonoContacto(
      empleado.extensionTelefonoProfesional,
      empleado.telefonoProfesional
    );
    const telefono = resolverContactoConFallback(
      this.canales.ordenTelefonoSMS,
      telefonoPersonal,
      telefonoProfesional
    );
    return { email, telefono };
  }

  private formatearTelefonoEmpleado(empleado: EmpleadoCrud): string {
    return this.resolverContactosDesdeEmpleado(empleado).telefono;
  }

  /**
   * Regla Envio simple para importacion de genericos:
   * - `tipoFirma` Validador/Sin firma no aplica y se limpia a `null`.
   * - Si tras limpiarlo no queda configuracion efectiva, el firmante se excluye.
   * - El firmante principal (`100`) siempre se conserva.
   */
  private sanitizarFirmantesImportados(
    firmantes: FirmanteNif[],
    principalTieneTiposContenido: boolean
  ): FirmanteNif[] {
    return firmantes.reduce<FirmanteNif[]>((acumulado, firmante) => {
      const codigo = firmante.tipoFirmante?.codigo;
      const esPrincipal = codigo === CODIGO_FIRMANTE_PRINCIPAL;
      const tipoFirma = firmante.firmas?.tipoFirma;
      const tipoNoPermitido =
        tipoFirma === TipoFirma.Validador || tipoFirma === TipoFirma.SinFirma;

      const firmanteNormalizado = tipoNoPermitido
        ? {
            ...firmante,
            firmas: { ...firmante.firmas, tipoFirma: null as unknown as TipoFirma }
          }
        : firmante;

      if (!tipoNoPermitido) {
        acumulado.push(firmanteNormalizado);
        return acumulado;
      }

      const tieneConfiguracionExtra =
        (firmanteNormalizado.formularios?.length ?? 0) > 0 ||
        (firmanteNormalizado.camposEditables?.length ?? 0) > 0 ||
        (esPrincipal && principalTieneTiposContenido);

      if (esPrincipal || tieneConfiguracionExtra) {
        acumulado.push(firmanteNormalizado);
      }

      return acumulado;
    }, []);
  }

  private sanitizarFirmantesImportadosEnFormulario(documentoId: string): void {
    const formulario = this.formulariosPorDocumento.get(documentoId);
    if (!formulario) {
      return;
    }

    const tiposContenido = (formulario.get('tiposContenido') as FormArray | null)?.value as
      | unknown[]
      | undefined;
    const firmantesActuales = ((formulario.get('firmantesNif') as FormArray | null)?.value ??
      []) as FirmanteNif[];
    const sanitizados = this.sanitizarFirmantesImportados(
      firmantesActuales,
      (tiposContenido?.length ?? 0) > 0
    );

    if (!this.hanCambiadoFirmantesImportados(firmantesActuales, sanitizados)) {
      return;
    }

    this.actualizarFirmantesEnFormulario(formulario, sanitizados);
  }

  private hanCambiadoFirmantesImportados(actuales: FirmanteNif[], sanitizados: FirmanteNif[]): boolean {
    if (actuales.length !== sanitizados.length) {
      return true;
    }
    return actuales.some((firmante, index) => {
      const saneado = sanitizados[index];
      if (!saneado) {
        return true;
      }
      return (
        firmante.tipoFirmante?.codigo !== saneado.tipoFirmante?.codigo ||
        firmante.firmas?.tipoFirma !== saneado.firmas?.tipoFirma
      );
    });
  }
}
